import { SELECTION_TYPES, TAB_TYPES, TAB_STATUS } from '../../../constants';
import i18n from '../../../i18n';

export const mutations = {

    /**
     * Adds a selection
    * A selection consists of information that can be shown in tabs in the informationPanel
    * @constructor
    * @param {object} location - contains information about the location of the results.
    * @param {array} [location.point] - specific location on the map.
    * @param {object} [location.any] - other property for location like geometry
    * @param {string} type - Sets the context for the results; layer for a click on the map, search for searches
    * @param {array.<Object>} results - contains information about the location of the results.
    * @param {object} refresh - contains information whith props to use action: createAndDisplaySelection.
    * @param {boolean} deletedState - is true when the selection is updated after features are deleted
    *
    * @typedef results
    * @type {object}
    * @param {array.<ol/Feature>} features - list of openlayers feature data
    * @param {object} layer layer data like the data in store.state.layers
    * @param {object} meta contains information about the result, can be used to for a tab in the informationpanel
    * @param {object} meta.type the type of result
    * @param {boolean} limitedResults is true when not all possible results have been returned from the backend
    * @param {object} [meta.theme] stores theme data like the data in store.state.themes
    * @param {object} [olLayer] the openlayers layer on the map linked to the result
    *
    */
    ADD_SELECTION(state, { location, type, results, refresh, deletedState = false }) {
        const resultsWithFeatures = results.filter(({ features }) => type === SELECTION_TYPES.FILTERED || features?.length > 0);
        const name = new Date().getTime();
        const selection = {
            name,
            location,
            results: resultsWithFeatures,
            type,
            refresh,
            deletedState
        };
        state.selections.unshift(selection);

        // Make sure there is a tabOrder for this selection
        resultsWithFeatures.forEach((result) => {
            const index = state.tabOrder.findIndex(i => i.title === result.layer.data.title);
            if (index === -1) {
                const tabOrderObj = {
                    title: result.layer.data.title,
                    type: TAB_TYPES.LAYER,
                    status: TAB_STATUS.READY,
                };
                state.tabOrder.push(tabOrderObj);
            } else {
                state.tabOrder[index].status = TAB_STATUS.READY;
            }
        });
    },
    ADD_SELECTION_OBJECT_DATA(state, { title, results }) {
        // first, remove previous selection
        state.selectionsObjectData = state.selectionsObjectData.filter(item => item.title !== title);
        // then, add selection
        const selection = {
            title,
            results
        };
        state.selectionsObjectData.unshift(selection);
    },
    ADD_LAYER_TAB(state, { title }) {
        const tabOrderObj = {
            title: title,
            type: TAB_TYPES.LAYER,
            status: TAB_STATUS.LOADING,
        };
        state.tabOrder.push(tabOrderObj);
    },
    CLEAR_SELECTION_OBJECT_DATA(state, { title }) {
        state.selectionsObjectData = state.selectionsObjectData.filter(item => item.title !== title);
    },
    CLEAR_SELECTIONS(state) {
        state.selections = [];
        state.selectionsObjectData = [];
        state.tabOrder = [];
        state.objectSearchResults = [];
        state.autoOpenRelations = true;
    },
    CLEAR_SELECTION_DATA(state, { title }) {
        if (!state.selections[0]?.results) {
            return;
        }

        // Delete data given de group title
        // Title is stored in state.selection[0].result[i].layer.data.title
        for (let i = 0; i < state.selections[0].results.length; i++) {
            if (state.selections[0].results[i].layer.data.title === title) {
                state.selections[0].results.splice(i, 1);
            }
        }
        // when results array is empty clear the selection.
        if (state.selections[0].results.length === 0) {
            state.selections = [];
        }

        // remove item from tabOrder
        if (title) {
            state.tabOrder = state.tabOrder.filter(item => item.title !== title);
        }

    },
    // resets the focus property
    // after displaying a result the focus is not relevant anymore
    // the result can be reused, therefore this reset exists
    RESET_SELECTIONS_FOCUS(state) {
        if (!state.selections[0]) {
            return;
        }
        state.selections[0].results.forEach(group => {
            if (group.meta) {
                group.meta.focus = undefined;
            }
        });
    },
    MARKING_SELECTION_AREA(state, markingSelectionArea) {
        state.markingSelectionArea = markingSelectionArea;
    },
    SET_SELECTION_POINTER(state, { group = null, index = 0 }) {
        state.selectedGroupIndex = typeof group === 'number' ? group : state.selectedGroupIndex;
        state.selectedItemIndex = index;
    },
    SET_POINTER(state, { location }) {
        if (location?.point) {
            const name = new Date().getTime();
            state.pointer = {
                name,
                location
            };
            state.pointerFocus = true;

            // Add pointer tab is there isn't one already
            const pointerTab = state.tabOrder.findIndex(t => t.title === i18n.t('pickedLocation'));
            if (pointerTab == -1) {
                const contextTab = {
                    title: i18n.t('pickedLocation'),
                    type: TAB_TYPES.POINTER,
                    status: TAB_STATUS.READY
                };
                state.tabOrder.unshift(contextTab);
            }
        } else {
            state.pointer = null;
        }

    },
    ACTIVATE_SELECTION_CANCELLATION_TOKEN(state, token) {
        state.selectionCancelTokens.push(token);
    },
    DEACTIVATE_SELECTION_CANCELLATION_TOKEN(state, token) {
        state.selectionCancelTokens = state.selectionCancelTokens.filter(_token => _token !== token);
    },
    SET_LAYER_SELECTION(state, { layerSelection }) {
        state.layerSelection = layerSelection;
    },
    SET_LOCATION_SELECTION(state, { locationSelection }) {
        state.locationSelection = locationSelection;
    },
    SET_RESIDENT_SELECTION(state, { residentSelection }) {
        state.residentSelection = residentSelection;
    },
    SET_COMPANY_SELECTION(state, { companySelection }) {
        state.companySelection = companySelection;
    },
    SET_ADDRESSATPOINT(state, addressAtPoint) {
        state.addressAtPoint = addressAtPoint;
    },
    SET_AUTO_OPEN_RELATIONS(state, value) {
        state.autoOpenRelations = value;
    },
};
